import React, { memo, useEffect, useState } from 'react'
import { useFormValidation } from '../../../hooks/useFormValidation'
import { buildControlsExt, integerInput } from '../../inputs/controls'
import { inputValidationCheck } from '../../../validators/inputValidators/inputValidators'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { inputDataDispatcher } from '../../../utils/InputDataDispatcher'
import { initInputData } from '../../../utils/InitInputData'
import { numberInputErrors } from '../../../utils/validationErrors/numberInputErrors'
import { useAppDispatch } from '../../../hooks/useAppDispatch'
import { PATH_JOINER } from '../utils/getConfigBitPath'
import { selectConfigBit, selectConfigIsChecked } from '../../../store/selectors/configSelectors'
import { INumberInputSchema } from '../types'
import { getLabel } from '../../../utils/getLabel'

export interface IDurationProps {
    dataPath: string
    pathWithElementID: string | undefined
    dataSchema: INumberInputSchema
    name: string
    wrapper?: string
}

export const Duration = memo(function Duration({
    dataPath,
    pathWithElementID,
    dataSchema,
    name,
    wrapper,
}: IDurationProps) {
    const dispatch = useAppDispatch()
    const configBit = useAppSelector(selectConfigBit(dataPath.split(PATH_JOINER)))
    const path = [...(dataPath.split(PATH_JOINER)), name]
    const [checkPath, setCheckPath] = useState<string[]>([])
    const isChecked = useAppSelector(selectConfigIsChecked)
    const {
        is_required: isRequired,
        name: inputLabel,
        type,
        description: tooltipText,
        format,
        default: defaultValue,
        change_warning: changeWarning,
    } = dataSchema
    const validationCheck = inputValidationCheck(type, dataSchema)
    const [
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult,
        isValid,
    ] = useFormValidation(configBit, { [name]: validationCheck })
    const [guid, , tabName] = path
    const label: string = getLabel(name, inputLabel)
    const className: string = !wrapper ? 'w-50' : ''
    const errorKey: string = pathWithElementID
        ? [...pathWithElementID.split(PATH_JOINER), name].join(PATH_JOINER)
        : path.join(PATH_JOINER)
    const errMsg = numberInputErrors(inputState?.[name], dataSchema)
    const isDataEmpty: number = Object.keys(configBit || {}).length
    const isUndefined: boolean = typeof configBit?.[name] === 'undefined'
    const isEmpty: boolean = !configBit || !isDataEmpty || isUndefined

    useEffect(() => {
        if (dataPath.length) {
            setCheckPath(dataPath.split(PATH_JOINER))
            initInputData(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    isRequired,
                    guid,
                    errorKey,
                    data: configBit,
                    setInputState,
                    tabName,
                },
                dispatch
            )
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (isChecked[guid] && dataPath.length) {
            isValid()
        }
    }, [isChecked]) // eslint-disable-line

    useEffect(() => {
        if (configBit && dataPath.length) {
            setInputState(configBit)
        }
    }, [configBit]) // eslint-disable-line

    useEffect(() => {
        if (dataPath.length) {
            inputDataDispatcher(
                {
                    isEmpty,
                    name,
                    path,
                    dataSchema,
                    data: configBit,
                    inputState,
                    validationCheck,
                    guid,
                    errorKey,
                    isUndefined,
                    checkPath,
                    type,
                },
                dispatch
            )
        }
    }, [touched[name], isChecked[guid]]) // eslint-disable-line

    const handleOnBlur = () => {
        inputDataDispatcher(
            {
                isEmpty,
                name,
                path,
                dataSchema,
                data: configBit,
                inputState,
                validationCheck,
                guid,
                errorKey,
                isUndefined,
                checkPath,
                type,
            },
            dispatch
        )
        setTouched && setTouched({ ...touched, [name]: true })
        if (inputState[name] === '') {
            setInputState((prev: any) => ({ ...prev, [name]: null }))
        }
    }

    const controlDescriptors = [
        integerInput({
            name,
            validationType: 'integer',
            tooltipText,
            placeholder: defaultValue || `${format}...`,
            label,
            className,
            isColumn: true,
            errMsg,
            isRequired,
            changeWarning,
            onBlur: handleOnBlur,
        }),
    ]

    const DurationInput = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    return <div className={`${!wrapper ? 'w-100' : ''}`}>{DurationInput}</div>
})
